// ═══════════════════════════════════════════════════════════
// ARAMS Dashboard — Project Detail Page
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { api, type StartRunConfig } from '@/lib/api';
import { formatDate, formatDuration, getPassRate } from '@/lib/utils';
import {
  Card,
  Badge,
  PageHeader,
  Button,
  StatCard,
  Skeleton,
  Input,
  Switch,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui';
import {
  Play,
  Users,
  TestTubes,
  Activity,
  Clock,
  BarChart3,
  Sparkles,
  ChevronRight,
  GitBranch,
  Globe,
  Settings,
  ArrowLeft,
  Blocks,
  Rocket,
  Monitor,
  RotateCw,
  Cpu,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function ProjectDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const projectId = parseInt(id!);
  const [showRunBuilder, setShowRunBuilder] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [runConfig, setRunConfig] = useState<Partial<StartRunConfig>>({
    browsers: ['chromium'],
    retries: 1,
    workers: 2,
    headed: false,
  });

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId),
    // Poll every 2s while any run is active; stop when all are terminal
    refetchInterval: (query) => {
      const runs = query.state.data?.recentRuns;
      if (!runs?.length) return false;
      const hasActive = runs.some(
        (r: { runStatus: string }) => r.runStatus === 'running' || r.runStatus === 'queued',
      );
      return hasActive ? 2000 : false;
    },
  });

  // Fetch branches from GitHub for the run builder dropdown
  const repoFullName = project?.projectGithubRepo || '';
  const [repoOwner, repoName] = repoFullName.includes('/') ? repoFullName.split('/') : ['', ''];
  const { data: branches } = useQuery({
    queryKey: ['github-branches', repoFullName],
    queryFn: () => api.listGithubBranches(repoOwner!, repoName!),
    enabled: !!repoOwner && !!repoName && showRunBuilder,
  });

  const startRun = useMutation({
    mutationFn: (config: StartRunConfig) => api.startRun(config),
    onSuccess: (run) => {
      toast.success(t('projectDetail.runStarted'));
      setShowRunBuilder(false);
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      navigate(`/projects/${projectId}/runs/${run.runId}`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteProject = useMutation({
    mutationFn: () => api.deleteProject(projectId),
    onSuccess: () => {
      toast.success(t('projectDetail.projectDeleted'));
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      navigate('/projects');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleLaunchRun = () => {
    startRun.mutate({
      projectId,
      ...runConfig,
    } as StartRunConfig);
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <Skeleton className="h-8 w-64 mb-8" />
        <div className="grid grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!project) return null;

  const lastRun = project.recentRuns?.[0];
  const passRate = lastRun
    ? getPassRate(lastRun.runPassedTests || 0, lastRun.runTotalTests || 0)
    : 0;
  const hasRuns = project.recentRuns && project.recentRuns.length > 0;

  // Parse AI config for suggested tests
  let aiConfig: any = null;
  try {
    if ((project as any).projectConfigYaml) {
      aiConfig = JSON.parse((project as any).projectConfigYaml);
    }
  } catch {
    /* ignore */
  }

  return (
    <div className="p-8">
      {/* Back button + Header */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/projects')} className="mb-4">
        <ArrowLeft className="w-4 h-4" />
        {t('common.backToProjects')}
      </Button>

      <PageHeader
        title={project.projectName}
        subtitle={project.projectDescription || project.projectSlug}
        actions={
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate(`/projects/${projectId}/analytics`)}>
              <BarChart3 className="w-4 h-4" />
              {t('projectDetail.analytics')}
            </Button>
            <Button variant="ghost" onClick={() => navigate(`/projects/${projectId}/ai`)}>
              <Sparkles className="w-4 h-4" />
              {t('projectDetail.aiAgent')}
            </Button>
            <Button
              variant="primary"
              loading={startRun.isPending}
              onClick={() => setShowRunBuilder(true)}
            >
              <Play className="w-4 h-4" />
              {t('projectDetail.runTests')}
            </Button>
          </div>
        }
      />

      {/* ── First Run CTA — shown when no runs exist ────── */}
      {!hasRuns && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <Card className="border-brand-500/20 bg-gradient-to-br from-brand-500/5 to-purple-500/5">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-500/20 to-purple-500/20 flex items-center justify-center border border-brand-500/20 flex-shrink-0">
                <Rocket className="w-8 h-8 text-brand-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-surface-100 mb-1">
                  {t('projectDetail.readyToTest')}
                </h3>
                <p className="text-sm text-surface-400 mb-4">
                  {t('projectDetail.readyToTestDesc')}
                </p>

                {/* Quick test suites from AI config */}
                {aiConfig?.suggestedTests?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-surface-500 mb-2 font-medium uppercase tracking-wider">
                      {t('projectDetail.aiSuggestedTests')} ({aiConfig.suggestedTests.length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {aiConfig.suggestedTests.slice(0, 8).map((test: any, i: number) => (
                        <span
                          key={i}
                          className="px-2.5 py-1 rounded-lg bg-surface-800/50 text-xs text-surface-300 border border-surface-700/50"
                        >
                          {test.name}
                        </span>
                      ))}
                      {aiConfig.suggestedTests.length > 8 && (
                        <span className="px-2.5 py-1 rounded-lg bg-surface-800/50 text-xs text-surface-500 border border-surface-700/50">
                          +{aiConfig.suggestedTests.length - 8} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <Button
                  variant="primary"
                  loading={startRun.isPending}
                  onClick={() => setShowRunBuilder(true)}
                  className="px-8 py-2.5"
                >
                  <Play className="w-5 h-5" />
                  {t('projectDetail.runTestsNow')}
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label={t('projects.passRate')}
          value={`${passRate}%`}
          icon={<Activity className="w-6 h-6" />}
          color={passRate >= 90 ? 'success' : passRate >= 70 ? 'warning' : 'danger'}
        />
        <StatCard
          label={t('projects.testRoles')}
          value={project.roles?.length || 0}
          icon={<Users className="w-6 h-6" />}
          color="brand"
        />
        <StatCard
          label={t('projects.suites')}
          value={project.suites?.length || 0}
          icon={<TestTubes className="w-6 h-6" />}
          color="warning"
        />
        <StatCard
          label={t('projectDetail.totalRuns')}
          value={project.recentRuns?.length || 0}
          icon={<Clock className="w-6 h-6" />}
          color="brand"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Recent Runs */}
        <div className="xl:col-span-2">
          <Card>
            <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-brand-400" />
              {t('projectDetail.recentRuns')}
            </h3>

            {project.recentRuns && project.recentRuns.length > 0 ? (
              <div className="space-y-2">
                {project.recentRuns.map((run, i) => (
                  <motion.div
                    key={run.runId}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => navigate(`/projects/${projectId}/runs/${run.runId}`)}
                    className="flex items-center justify-between p-3 rounded-lg bg-surface-800/30 hover:bg-surface-800/60 cursor-pointer transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <Badge
                        variant={
                          run.runStatus === 'passed'
                            ? 'success'
                            : run.runStatus === 'failed'
                              ? 'danger'
                              : run.runStatus === 'running'
                                ? 'info'
                                : 'outline'
                        }
                      >
                        {run.runStatus}
                      </Badge>
                      <div>
                        <span className="text-sm text-surface-200">
                          {t('projectDetail.runLabel', { id: run.runId })}
                        </span>
                        {run.runBranch && (
                          <span className="text-xs text-surface-500 ml-2">
                            <GitBranch className="w-3 h-3 inline mr-0.5" />
                            {run.runBranch}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right text-xs">
                        <div className="text-surface-300">
                          {run.runPassedTests || 0}/{run.runTotalTests || 0}
                          <span className="text-surface-500 ml-1">{t('common.passed')}</span>
                        </div>
                        {run.runDurationMs && (
                          <div className="text-surface-500">
                            {formatDuration(run.runDurationMs)}
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-surface-500">
                        {run.runCreatedAt ? formatDate(run.runCreatedAt) : ''}
                      </span>
                      <ChevronRight className="w-4 h-4 text-surface-600 group-hover:text-brand-400 transition-colors" />
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-surface-500 text-center py-8">
                {t('projectDetail.noRunsHint')}
              </p>
            )}
          </Card>
        </div>

        {/* Sidebar — Roles & Suites */}
        <div className="space-y-6">
          {/* Project Info */}
          <Card>
            <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-brand-400" />
              {t('projectDetail.configuration')}
            </h3>
            <div className="space-y-3 text-sm">
              {project.projectTargetUrl && (
                <div className="flex items-center gap-2 text-surface-400">
                  <Globe className="w-4 h-4" />
                  <span className="truncate">{project.projectTargetUrl}</span>
                </div>
              )}
              {project.projectGithubRepo && (
                <div className="flex items-center gap-2 text-surface-400">
                  <GitBranch className="w-4 h-4" />
                  <span className="truncate">{project.projectGithubRepo}</span>
                </div>
              )}
            </div>
          </Card>

          {/* Test Roles */}
          <Card>
            <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-brand-400" />
              {t('projectDetail.testRolesCount', { count: project.roles?.length || 0 })}
            </h3>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {project.roles?.map((role) => (
                <div
                  key={role.roleId}
                  className="flex items-center justify-between p-2 rounded-lg bg-surface-800/30"
                >
                  <div>
                    <div className="text-sm font-medium text-surface-200">{role.roleName}</div>
                    <div className="text-xs text-surface-500">{role.roleEmail}</div>
                  </div>
                  {role.roleBusinessType && (
                    <Badge variant="outline">{role.roleBusinessType}</Badge>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Test Suites */}
          <Card>
            <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
              <Blocks className="w-5 h-5 text-brand-400" />
              {t('projectDetail.testSuitesCount', { count: project.suites?.length || 0 })}
            </h3>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {project.suites?.map((suite) => (
                <div
                  key={suite.suiteId}
                  className="flex items-center justify-between p-2 rounded-lg bg-surface-800/30"
                >
                  <div>
                    <div className="text-sm font-medium text-surface-200">{suite.suiteName}</div>
                    <div className="text-xs text-surface-500 flex gap-1 mt-0.5">
                      {suite.suiteTags?.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Badge
                    variant={
                      suite.suitePriority === 'critical'
                        ? 'danger'
                        : suite.suitePriority === 'high'
                          ? 'warning'
                          : 'outline'
                    }
                    className="text-[10px]"
                  >
                    {suite.suitePriority}
                  </Badge>
                </div>
              ))}
            </div>
          </Card>

          {/* Danger Zone — Delete Project */}
          <Card className="border border-danger/20">
            <h3 className="text-lg font-semibold text-danger mb-2 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Danger Zone
            </h3>
            <p className="text-sm text-surface-400 mb-4">
              {t('projectDetail.deleteProjectWarning')}
            </p>
            <Button variant="danger" onClick={() => setShowDeleteConfirm(true)} className="w-full">
              <Trash2 className="w-4 h-4" />
              {t('projectDetail.deleteProject')}
            </Button>
          </Card>
        </div>
      </div>

      {/* ── Delete Confirmation Dialog ──────────────────────────── */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-sm text-center">
          <DialogHeader className="items-center">
            <div className="w-14 h-14 rounded-full bg-danger/10 flex items-center justify-center mx-auto mb-2">
              <Trash2 className="w-7 h-7 text-danger" />
            </div>
            <DialogTitle>{t('projectDetail.deleteProject')}</DialogTitle>
            <DialogDescription>
              {t('projectDetail.deleteProjectConfirm')}
              <br />
              <span className="text-danger font-medium">
                {t('projectDetail.deleteProjectWarning')}
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center gap-3">
            <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              loading={deleteProject.isPending}
              onClick={() => deleteProject.mutate()}
            >
              <Trash2 className="w-4 h-4" />
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Run Builder Dialog ─────────────────────────────── */}
      <Dialog open={showRunBuilder} onOpenChange={setShowRunBuilder}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="w-5 h-5 text-brand-400" />
              {t('settings.runBuilder')}
            </DialogTitle>
            <DialogDescription>{t('settings.runBuilderDesc')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Branch — dropdown from GitHub */}
            <Select
              value={runConfig.branch || project?.projectGithubBranch || 'main'}
              onValueChange={(v: string) => setRunConfig({ ...runConfig, branch: v })}
            >
              <SelectTrigger label={t('settings.selectBranch')}>
                <div className="flex items-center gap-2">
                  <GitBranch className="w-4 h-4 text-surface-400" />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent>
                {branches && branches.length > 0 ? (
                  branches.map((b: any) => (
                    <SelectItem key={b.name} value={b.name}>
                      {b.name}
                      {b.protected ? ' 🔒' : ''}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value={runConfig.branch || project?.projectGithubBranch || 'main'}>
                    {runConfig.branch || project?.projectGithubBranch || 'main'}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>

            {/* Browsers */}
            <div>
              <label className="text-sm font-medium text-surface-300 mb-2 block">
                {t('settings.selectBrowsers')}
              </label>
              <div className="flex gap-2">
                {['chromium', 'firefox', 'webkit'].map((b) => {
                  const selected = runConfig.browsers?.includes(b);
                  return (
                    <Button
                      key={b}
                      variant={selected ? 'outline' : 'ghost'}
                      size="sm"
                      onClick={() => {
                        const current = runConfig.browsers || [];
                        setRunConfig({
                          ...runConfig,
                          browsers: selected ? current.filter((x) => x !== b) : [...current, b],
                        });
                      }}
                      className={
                        selected ? 'border-brand-500/30 bg-brand-600/20 text-brand-400' : ''
                      }
                    >
                      <Monitor className="w-3.5 h-3.5" />
                      {b}
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Suites */}
            {project?.suites && project.suites.length > 0 && (
              <div>
                <label className="text-sm font-medium text-surface-300 mb-2 block">
                  {t('settings.selectSuites')}
                </label>
                <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                  {project.suites.map((suite) => {
                    const selected = runConfig.suiteIds?.includes(suite.suiteId);
                    return (
                      <Button
                        key={suite.suiteId}
                        variant={selected ? 'outline' : 'ghost'}
                        onClick={() => {
                          const current = runConfig.suiteIds || [];
                          setRunConfig({
                            ...runConfig,
                            suiteIds: selected
                              ? current.filter((x) => x !== suite.suiteId)
                              : [...current, suite.suiteId],
                          });
                        }}
                        className={`w-full justify-between ${
                          selected
                            ? 'border-brand-500/20 bg-brand-600/15 text-brand-400'
                            : 'text-surface-300'
                        }`}
                      >
                        <span className="font-medium">{suite.suiteName}</span>
                        <Badge variant={selected ? 'brand' : 'outline'} className="text-[10px]">
                          {suite.suiteType}
                        </Badge>
                      </Button>
                    );
                  })}
                </div>
                <p className="text-xs text-surface-500 mt-1">
                  {!runConfig.suiteIds?.length
                    ? t('settings.allSuites')
                    : `${runConfig.suiteIds.length} selected`}
                </p>
              </div>
            )}

            {/* Config row */}
            <div className="grid grid-cols-3 gap-3">
              <Input
                label={t('settings.retries')}
                type="number"
                value={String(runConfig.retries ?? 1)}
                onChange={(e) =>
                  setRunConfig({ ...runConfig, retries: parseInt(e.target.value) || 0 })
                }
                leftIcon={<RotateCw className="w-3.5 h-3.5" />}
              />
              <Input
                label={t('settings.workers')}
                type="number"
                value={String(runConfig.workers ?? 2)}
                onChange={(e) =>
                  setRunConfig({ ...runConfig, workers: parseInt(e.target.value) || 1 })
                }
                leftIcon={<Cpu className="w-3.5 h-3.5" />}
              />
              <div>
                <label className="text-sm font-medium text-surface-300 mb-2 block">
                  {t('settings.headed')}
                </label>
                <Switch
                  checked={runConfig.headed || false}
                  onCheckedChange={(v: boolean) => setRunConfig({ ...runConfig, headed: v })}
                  label={runConfig.headed ? 'On' : 'Off'}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRunBuilder(false)}>
              {t('common.cancel')}
            </Button>
            <Button loading={startRun.isPending} onClick={handleLaunchRun}>
              <Rocket className="w-4 h-4" />
              {t('settings.launchRun')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
